import React from 'react';
import './CheckoutCart.css';
import {
  availableGroups, productsOf, platformsOf, termsOf, findPlan, formatAmount, cycleSuffix, productLabel,
  CREDITS_GROUP, SUPPORTED_CURRENCIES,
} from '../lib/catalogue';

/**
 * Step 1: what to buy. An edition first (CORE / PROFESSIONAL / INFINITY / EDUCATION, or
 * plugin credits), then the plugin it runs in (Rhino / Grasshopper, nTop, Fusion 360), the
 * term (monthly / annual where sold) and the number of users. Every choice maps to one
 * catalogue product (one Paddle price); users is the Paddle quantity.
 */
const CheckoutCart = ({ products, product, quantity, notes, onSelect, onQuantityChange, onEdit, currency, onCurrencyChange, loading }) => {
  const groups = availableGroups(products);
  const isCredits = product.groupCode === CREDITS_GROUP.code;
  const group = groups.find((g) => g.code === product.groupCode) || (isCredits ? CREDITS_GROUP : null);
  const platforms = isCredits ? [] : platformsOf(products, product.groupCode);
  const terms = isCredits ? [] : termsOf(products, product.groupCode, product.platform);
  const packs = isCredits ? productsOf(products, CREDITS_GROUP.code) : [];

  // Switching the edition keeps the plugin and term when the new edition sells them.
  const chooseGroup = (code) => {
    if (code === product.groupCode) return;
    const pool = productsOf(products, code);
    if (code === CREDITS_GROUP.code) {
      onSelect(pool[0]);
      return;
    }
    const platform = pool.some((p) => p.platform === product.platform)
      ? product.platform
      : (platformsOf(products, code)[0] || {}).code;
    const available = termsOf(products, code, platform);
    const months = available.some((t) => t.months === product.termMonths)
      ? product.termMonths
      : (available[available.length - 1] || {}).months;
    onSelect(findPlan(products, code, platform, months) || pool[0]);
  };

  const choosePlatform = (platform) => {
    if (platform === product.platform) return;
    const available = termsOf(products, product.groupCode, platform);
    const months = available.some((t) => t.months === product.termMonths)
      ? product.termMonths
      : (available[available.length - 1] || {}).months;
    onSelect(findPlan(products, product.groupCode, platform, months));
  };

  const chooseTerm = (e) => {
    onSelect(findPlan(products, product.groupCode, product.platform, Number(e.target.value)));
  };

  const choosePack = (e) => {
    onSelect(packs.find((p) => p.sku === e.target.value));
  };

  const unitPrice = loading ? 'Loading...' : (product.price_display || '—');
  const total = loading
    ? 'Loading...'
    : (Number.isFinite(product.price_numeric)
      ? `${formatAmount(product.price_numeric * quantity, product.currency)} ${cycleSuffix(product)}`.trim()
      : (product.price_display || '—'));
  const seatsHint = isCredits
    ? null
    : (product.seatsPerUnit > 1
      ? `Each licence includes ${product.seatsPerUnit} users.`
      : 'One user per licence. Buy one licence per user.');

  return (
    <div className="checkout-step active">
      <div className="step-header">
        <div className="step-number-container">
          <div className="step-number">1</div>
        </div>
        <h3>Cart</h3>
        <button className="edit-btn" onClick={onEdit}>Edit</button>
      </div>
      <div className="step-content">
        {notes && notes.length > 0 && (
          <div className="cart-notes" role="status">
            {notes.map((n) => <p key={n}>{n}</p>)}
          </div>
        )}

        <div className="cart-item">
          <div className="control-group">
            <label>Edition</label>
            <div className="group-grid" role="radiogroup" aria-label="Edition">
              {groups.map((g) => (
                <button
                  type="button"
                  key={g.code}
                  role="radio"
                  aria-checked={g.code === product.groupCode}
                  className={`group-card ${g.code === product.groupCode ? 'selected' : ''}`}
                  onClick={() => chooseGroup(g.code)}
                >
                  <img src={g.logo} alt="" className="group-logo" />
                  <span className="group-title">{g.title}</span>
                  <span className="group-blurb">{g.blurb}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="product-info">
            <img src={group ? group.logo : '/logo192.png'} alt="" className="product-icon" />
            <div className="product-details">
              <h4>{productLabel(product)}</h4>
              <p className="product-name">{product.sku}{seatsHint ? ` · ${seatsHint}` : ''}</p>
            </div>
          </div>

          <div className="cart-controls">
            {!isCredits && (
              <div className="control-group">
                <label>Plugin</label>
                <div className="segmented" role="radiogroup" aria-label="Plugin">
                  {platforms.map((p) => (
                    <button
                      type="button"
                      key={p.code}
                      role="radio"
                      aria-checked={p.code === product.platform}
                      className={p.code === product.platform ? 'active' : ''}
                      onClick={() => choosePlatform(p.code)}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isCredits && (
              <div className="control-group">
                <label>Term</label>
                <select value={product.termMonths} onChange={chooseTerm} disabled={terms.length <= 1}>
                  {terms.map((t) => (
                    <option key={t.months} value={t.months}>{t.label}</option>
                  ))}
                </select>
              </div>
            )}

            {isCredits && (
              <div className="control-group">
                <label>Pack</label>
                <select value={product.sku} onChange={choosePack} disabled={packs.length <= 1}>
                  {packs.map((p) => (
                    <option key={p.sku} value={p.sku}>{productLabel(p)}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="control-group">
              <label>{isCredits ? 'Quantity' : 'Users'}</label>
              <input
                type="number"
                className="quantity-input"
                min={1}
                max={product.maxQuantity}
                step={1}
                value={quantity}
                readOnly={product.maxQuantity <= 1}
                onChange={(e) => onQuantityChange(e.target.value)}
                aria-label={isCredits ? 'Quantity' : 'Number of users'}
              />
            </div>

            <div className="control-group" style={{ marginLeft: 'auto', alignItems: 'flex-end' }}>
              <label>Total</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="price-display" style={{ padding: 0 }}>{total}</div>
                <select
                  value={currency}
                  onChange={onCurrencyChange}
                  className="currency-select-inline"
                  aria-label="Currency"
                  style={{
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {!isCredits && quantity > 1 && (
                <span className="hint">{unitPrice} per user</span>
              )}
              {product.billingPeriod && (
                <span className="hint">Renews automatically. Prices exclude VAT.</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutCart;
